/**
 * Statistics written the APA 7 way.
 *
 * The rules come from the APA Style "Numbers and Statistics Guide" (7th ed., updated
 * September 2024) and the Publication Manual sections it cites: §6.36 (decimal places),
 * §6.40–6.45 (reporting statistics) and Table 6.5 (statistical symbols):
 *
 * - Italic type for letters used as statistical symbols (t, F, p, r, M, SD, N, d, R);
 *   standard type for Greek letters (χ², η², β) and for abbreviations such as OR and CI.
 * - No zero before the decimal point when the statistic cannot exceed 1 (p, r, R², η²,
 *   Cramér's V, proportions); a zero when it can (t, F, χ², z, d, M, SD, b, OR).
 * - Two decimals for inferential statistics, correlations and effect sizes; exact p to
 *   two or three decimals, and "p < .001" below that.
 * - A space before and after an operator (=, <); a minus sign directly against its number.
 * - No comma in degrees of freedom; a comma in other figures of 1,000 or more.
 * - A confidence interval as "95% CI [lower, upper]".
 *
 * Text is built as segments (symbol, italic, superscript) so the same result can be shown
 * on the page, copied with its formatting into Word, or copied as plain text.
 */

export interface Segment {
  text: string;
  italic?: boolean;
  sup?: boolean;
  sub?: boolean;
}

export type StatType =
  | "t"
  | "f"
  | "chi"
  | "r"
  | "z"
  | "mean"
  | "model"
  | "coef"
  | "or";

export type FieldKey =
  | "t"
  | "df"
  | "df1"
  | "df2"
  | "chi"
  | "N"
  | "F"
  | "r"
  | "z"
  | "M"
  | "SD"
  | "R2"
  | "b"
  | "beta"
  | "OR"
  | "p"
  | "d"
  | "V"
  | "eta"
  | "ciLevel"
  | "ciLow"
  | "ciHigh";

export interface Range {
  min?: number;
  max?: number;
  /** The bound itself is not allowed. */
  exclusiveMin?: boolean;
}

/** What each field may hold. */
export const FIELD_RANGES: Record<FieldKey, Range> = {
  t: {},
  df: { min: 0, exclusiveMin: true },
  df1: { min: 0, exclusiveMin: true },
  df2: { min: 0, exclusiveMin: true },
  chi: { min: 0 },
  N: { min: 1 },
  F: { min: 0 },
  r: { min: -1, max: 1 },
  z: {},
  M: {},
  SD: { min: 0 },
  R2: { min: 0, max: 1 },
  b: {},
  beta: {},
  OR: { min: 0, exclusiveMin: true },
  p: { min: 0, max: 1 },
  d: {},
  V: { min: 0, max: 1 },
  eta: { min: 0, max: 1 },
  ciLevel: { min: 0, max: 100, exclusiveMin: true },
  ciLow: {},
  ciHigh: {},
};

interface Spec {
  required: FieldKey[];
  optional: FieldKey[];
}

/** The fields each result type asks for, in the order they appear in the output. */
export const STAT_FIELDS: Record<StatType, Spec> = {
  t: {
    required: ["t", "df", "p"],
    optional: ["d", "ciLow", "ciHigh", "ciLevel"],
  },
  f: { required: ["F", "df1", "df2", "p"], optional: ["eta"] },
  chi: { required: ["chi", "df", "p"], optional: ["N", "V"] },
  r: { required: ["r", "df", "p"], optional: ["ciLow", "ciHigh", "ciLevel"] },
  z: { required: ["z", "p"], optional: ["ciLow", "ciHigh", "ciLevel"] },
  mean: { required: ["M", "SD"], optional: [] },
  model: { required: ["R2", "F", "df1", "df2", "p"], optional: [] },
  coef: {
    required: ["b", "t", "df", "p"],
    optional: ["beta", "ciLow", "ciHigh", "ciLevel"],
  },
  or: { required: ["OR", "p"], optional: ["ciLow", "ciHigh", "ciLevel"] },
};

export interface FormatOptions {
  /** Exact p to three decimals, or two when p ≥ .01 (APA allows either). */
  pDecimals: 2 | 3;
  /** Decimals for M and SD: one for whole-number scales, two otherwise. */
  meanDecimals: 1 | 2;
  /** A hyphen-minus for negatives instead of the true minus sign U+2212. */
  plainMinus: boolean;
}

export const DEFAULT_OPTIONS: FormatOptions = {
  pDecimals: 3,
  meanDecimals: 2,
  plainMinus: false,
};

export type FormatError =
  | { kind: "missing"; field: FieldKey }
  | { kind: "invalid"; field: FieldKey }
  | { kind: "range"; field: FieldKey }
  | { kind: "interval" };

export type FormatResult =
  | { ok: true; segments: Segment[] }
  | { ok: false; error: FormatError };

/* ------------------------------------------------------------ number text */

const MINUS = "−";
const LEADING_ZERO = /^(-?)0(?=\.)/;

/** A number at fixed decimals, optionally without the zero before the point. */
export function fixedText(
  value: number,
  decimals: number,
  options: { leadingZero: boolean; plainMinus: boolean; grouping?: boolean }
): string {
  let text = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: options.grouping ?? false,
  }).format(Math.abs(value));
  if (!options.leadingZero) {
    text = text.replace(LEADING_ZERO, "$1");
  }
  const rounded = Number(text.replaceAll(",", ""));
  const negative = value < 0 && rounded !== 0;
  return negative ? `${options.plainMinus ? "-" : MINUS}${text}` : text;
}

/** Degrees of freedom: whole numbers bare, fractional (Welch) to two decimals, no comma. */
export function dfText(value: number, plainMinus: boolean): string {
  return Number.isInteger(value)
    ? String(value)
    : fixedText(value, 2, { leadingZero: true, plainMinus });
}

/** `{ relation, text }` for p: "= .032", "< .001", "> .999". */
export function pParts(
  p: number,
  decimals: 2 | 3
): { relation: "=" | "<" | ">"; text: string } {
  const plain = { leadingZero: false, plainMinus: false };
  if (p < 0.001) {
    return { relation: "<", text: fixedText(0.001, 3, plain) };
  }
  if (p >= 0.9995) {
    return { relation: ">", text: fixedText(0.999, 3, plain) };
  }
  const places = decimals === 2 && p >= 0.01 ? 2 : 3;
  const text = fixedText(p, places, plain);
  // Two decimals can round up to 1.00; fall back to three so p never reads as 1.
  if (places === 2 && text === "1.00") {
    return { relation: "=", text: fixedText(p, 3, plain) };
  }
  return { relation: "=", text };
}

/* --------------------------------------------------------------- segments */

const word = (text: string): Segment => ({ text });
const symbol = (text: string): Segment => ({ text, italic: true });
const greek = (text: string): Segment => ({ text });
const squared = (): Segment => ({ text: "2", sup: true });

function operator(relation: string): Segment {
  return word(` ${relation} `);
}

function join(parts: Segment[][]): Segment[] {
  return parts.flatMap((part, index) =>
    index === 0 ? part : [word(", "), ...part]
  );
}

const SUP_TWO = "²";

export function toPlainText(segments: readonly Segment[]): string {
  return segments
    .map((segment) =>
      segment.sup && segment.text === "2" ? SUP_TWO : segment.text
    )
    .join("");
}

const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
};
const HTML_SPECIAL = /[&<>]/g;

export function toHtml(segments: readonly Segment[]): string {
  const body = segments
    .map((segment) => {
      let html = segment.text.replace(
        HTML_SPECIAL,
        (char) => HTML_ESCAPES[char] ?? char
      );
      if (segment.italic) {
        html = `<i>${html}</i>`;
      }
      if (segment.sup) {
        html = `<sup>${html}</sup>`;
      }
      if (segment.sub) {
        html = `<sub>${html}</sub>`;
      }
      return html;
    })
    .join("");
  return `<p>${body}</p>`;
}

/* ----------------------------------------------------------------- parsing */

type Values = Partial<Record<FieldKey, string>>;

function parseField(
  key: FieldKey,
  raw: string | undefined,
  parse: (text: string) => number | null
): { value: number | null; error: FormatError | null } {
  if (raw === undefined || raw.trim() === "") {
    return { value: null, error: null };
  }
  const value = parse(raw);
  if (value === null) {
    return { value: null, error: { kind: "invalid", field: key } };
  }
  const range = FIELD_RANGES[key];
  const belowMin =
    range.min !== undefined &&
    (range.exclusiveMin ? value <= range.min : value < range.min);
  if (belowMin || (range.max !== undefined && value > range.max)) {
    return { value: null, error: { kind: "range", field: key } };
  }
  return { value, error: null };
}

type Parsed = Partial<Record<FieldKey, number>>;

function readFields(
  type: StatType,
  values: Values,
  parse: (text: string) => number | null
): { parsed: Parsed } | { error: FormatError } {
  const spec = STAT_FIELDS[type];
  const parsed: Parsed = {};
  for (const key of [...spec.required, ...spec.optional]) {
    const { value, error } = parseField(key, values[key], parse);
    if (error) {
      return { error };
    }
    if (value === null) {
      if (spec.required.includes(key)) {
        return { error: { kind: "missing", field: key } };
      }
      continue;
    }
    parsed[key] = value;
  }
  return { parsed };
}

/* ---------------------------------------------------------------- building */

interface Context {
  options: FormatOptions;
  parsed: Parsed;
}

function number(
  value: number,
  ctx: Context,
  config: { decimals?: number; leadingZero?: boolean; grouping?: boolean } = {}
): string {
  return fixedText(value, config.decimals ?? 2, {
    leadingZero: config.leadingZero ?? true,
    plainMinus: ctx.options.plainMinus,
    grouping: config.grouping,
  });
}

function stat(
  label: Segment[],
  value: number,
  ctx: Context,
  config: { decimals?: number; leadingZero?: boolean } = {}
): Segment[] {
  return [...label, operator("="), word(number(value, ctx, config))];
}

function pPart(ctx: Context): Segment[] {
  const { relation, text } = pParts(ctx.parsed.p ?? 1, ctx.options.pDecimals);
  return [symbol("p"), operator(relation), word(text)];
}

function dfPart(df: number, ctx: Context): string {
  return dfText(df, ctx.options.plainMinus);
}

function intervalPart(
  ctx: Context,
  leadingZero: boolean
): Segment[] | "interval" | null {
  const { ciLow, ciHigh, ciLevel } = ctx.parsed;
  if (ciLow === undefined && ciHigh === undefined) {
    return null;
  }
  if (ciLow === undefined || ciHigh === undefined || ciLow > ciHigh) {
    return "interval";
  }
  const level = ciLevel ?? 95;
  const low = number(ciLow, ctx, { leadingZero });
  const high = number(ciHigh, ctx, { leadingZero });
  return [word(`${level}% CI [${low}, ${high}]`)];
}

type Builder = (ctx: Context) => Segment[][];

const BUILDERS: Record<StatType, Builder> = {
  t: (ctx) => {
    const { t, df, d } = ctx.parsed;
    return [
      [
        symbol("t"),
        word(`(${dfPart(df ?? 0, ctx)})`),
        operator("="),
        word(number(t ?? 0, ctx)),
      ],
      pPart(ctx),
      ...(d === undefined ? [] : [stat([symbol("d")], d, ctx)]),
    ];
  },
  f: (ctx) => {
    const { F, df1, df2, eta } = ctx.parsed;
    return [
      [
        symbol("F"),
        word(`(${dfPart(df1 ?? 0, ctx)}, ${dfPart(df2 ?? 0, ctx)})`),
        operator("="),
        word(number(F ?? 0, ctx)),
      ],
      pPart(ctx),
      ...(eta === undefined
        ? []
        : [
            stat(
              [greek("η"), { text: "p", italic: true, sub: true }, squared()],
              eta,
              ctx,
              { leadingZero: false }
            ),
          ]),
    ];
  },
  chi: (ctx) => {
    const { chi, df, N, V } = ctx.parsed;
    const label: Segment[] = [greek("χ"), squared()];
    const opening =
      N === undefined
        ? [word(`(${dfPart(df ?? 0, ctx)})`)]
        : [
            word(`(${dfPart(df ?? 0, ctx)}, `),
            symbol("N"),
            word(` = ${number(N, ctx, { decimals: 0, grouping: true })})`),
          ];
    return [
      [...label, ...opening, operator("="), word(number(chi ?? 0, ctx))],
      pPart(ctx),
      ...(V === undefined
        ? []
        : [stat([symbol("V")], V, ctx, { leadingZero: false })]),
    ];
  },
  r: (ctx) => {
    const { r, df } = ctx.parsed;
    return [
      [
        symbol("r"),
        word(`(${dfPart(df ?? 0, ctx)})`),
        operator("="),
        word(number(r ?? 0, ctx, { leadingZero: false })),
      ],
      pPart(ctx),
    ];
  },
  z: (ctx) => [stat([symbol("z")], ctx.parsed.z ?? 0, ctx), pPart(ctx)],
  mean: (ctx) => {
    const decimals = ctx.options.meanDecimals;
    return [
      stat([symbol("M")], ctx.parsed.M ?? 0, ctx, { decimals }),
      stat([symbol("SD")], ctx.parsed.SD ?? 0, ctx, { decimals }),
    ];
  },
  model: (ctx) => {
    const { R2, F, df1, df2 } = ctx.parsed;
    return [
      stat([symbol("R"), squared()], R2 ?? 0, ctx, { leadingZero: false }),
      [
        symbol("F"),
        word(`(${dfPart(df1 ?? 0, ctx)}, ${dfPart(df2 ?? 0, ctx)})`),
        operator("="),
        word(number(F ?? 0, ctx)),
      ],
      pPart(ctx),
    ];
  },
  coef: (ctx) => {
    const { b, beta, t, df } = ctx.parsed;
    return [
      stat([symbol("b")], b ?? 0, ctx),
      ...(beta === undefined
        ? []
        : [
            stat([greek("β")], beta, ctx, { leadingZero: Math.abs(beta) >= 1 }),
          ]),
      [
        symbol("t"),
        word(`(${dfPart(df ?? 0, ctx)})`),
        operator("="),
        word(number(t ?? 0, ctx)),
      ],
      pPart(ctx),
    ];
  },
  or: (ctx) => [
    [word("OR"), operator("="), word(number(ctx.parsed.OR ?? 0, ctx))],
    pPart(ctx),
  ],
};

/** Whether the confidence interval's numbers can exceed 1 in size (so keep the zero). */
const INTERVAL_KEEPS_ZERO: Record<StatType, boolean> = {
  t: true,
  f: true,
  chi: true,
  r: false,
  z: true,
  mean: true,
  model: true,
  coef: true,
  or: true,
};

/**
 * One result, formatted. `parse` reads the typed numbers (the widget passes the
 * locale-aware `parseNumberInput`); blank optional fields are skipped.
 */
export function formatStatistic(
  type: StatType,
  values: Values,
  options: FormatOptions,
  parse: (text: string) => number | null
): FormatResult {
  const read = readFields(type, values, parse);
  if ("error" in read) {
    return { ok: false, error: read.error };
  }
  const ctx: Context = { options, parsed: read.parsed };
  const parts = BUILDERS[type](ctx);
  const interval = intervalPart(ctx, INTERVAL_KEEPS_ZERO[type]);
  if (interval === "interval") {
    return { ok: false, error: { kind: "interval" } };
  }
  if (interval) {
    // A coefficient's interval belongs to b, which comes first; elsewhere it closes the result.
    parts.splice(type === "coef" ? 1 : parts.length, 0, interval);
  }
  return { ok: true, segments: join(parts) };
}
